import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { FocusError } from "focus-formik-error";
import { useFormik } from "formik";
import React, { memo, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { DateRangePicker } from "materialui-daterange-picker";
import { defaultApplicantsData } from "../../../_utilities/defaultfieldsModel";
import ApplicantsDynamicTable from "../../../components/DynamicTable/ApplicantsDynamicTable";
import CheckIcon from "@mui/icons-material/Check";
import { DashboardPlusicon } from "../../../Icons/DashboardPlusicon";

import * as _info from "../../../_utilities/getUserInfo";
import { PORTAL_SOURCES, safeJSONParse } from "../../../_utilities/constants";
import { normalizeValue } from "../../../_utilities/utilities";

const ApplicantDetailsComponent = ({
  componentType,
  input,
  iniValue,
  actions,
  onValidData,
  disableSubmitBtn,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const customFieldsData = useSelector((state) => state.CustomFields.data);

  const [getDetails, setGetDetails] = useState(iniValue);
  const [disableBtn, setDisableBtn] = useState(disableSubmitBtn);
  const [isPortals, setIsPortals] = useState(false);
  const [portalFrom, setPortalFrom] = useState("");
  const [addSkills, setAddSkills] = useState("");
  const [addCertification, setAddCertification] = useState("");
  const [sourceModelValues, setSourceModelValues] = useState([]);
  const [candidateStatusValues, setCandidateStatusValues] = useState([]);
  const [open, setOpen] = React.useState(false);
  const toggle = () => setOpen(!open);
  const [dateRange, setDateRange] = React.useState({});

  const [customFieldsArray, setCustomFieldsArray] = useState([]);
  const [getcustomFields, setGetCustomFields] = useState({});

  /* -------------------- FORMik -------------------- */

  const validationSchema = Yup.object({
    firstName: Yup.string()
      .trim()
      .matches(/^[A-Za-z ]*$/, "Please enter valid name")
      .required("Please enter first name"),
    lastName: Yup.string()
      .trim()
      .matches(/^[A-Za-z ]*$/, "Please enter valid name")
      .required("Please enter last name"),
    contactNumber: Yup.string()
      .matches(/^[0-9()+-\s]*$/, "Invalid contact number")
      .min(10)
      .required("Please enter contact number"),
    emailId: Yup.string().email("Invalid email").required("Please enter email"),
    resumesourceId: Yup.string().required("Please select source"),
    // candidateStatus: Yup.string().required('Please select status'),
    linkedIn: Yup.string().matches(
      /^(https?:\/\/)?(www\.|\w{2}\.)?linkedin\.com\/(in|company)\/[a-zA-Z0-9_-]+\/?$/,
      "Invalid LinkedIn URL",
    ),
    formattedLocation: Yup.string().matches(
      /^[a-zA-Z0-9\s,#.&/-]*$/,
      "Please enter a valid address",
    ),
    locationCity: Yup.string().matches(/^[A-Za-z ]*$/,"Please enter valid city name"),
    locationCountry: Yup.string().matches(/^[A-Za-z ]*$/,"Please enter valid country name"),
    locationState: Yup.string().matches(/^[A-Za-z ]*$/,"Please enter valid state name"),
    latitude: Yup.number()
      .typeError("Latitude must be a number")
      .min(-90, "Latitude must be between -90 and 90")
      .max(90, "Latitude must be between -90 and 90"),
    longitude: Yup.number()
      .typeError("Longitude must be a number")
      .min(-180, "Longitude must be between -180 and 180")
      .max(180, "Longitude must be between -180 and 180"),
  });

  // Build errors for mandatory custom fields
  const validateMandatoryCustomFields = (fieldsArray = [], valuesObj = {}) => {
    valuesObj = valuesObj || {};
    const cfErrors = {};
    fieldsArray.forEach((f) => {
      if (Number(f.mandatory) !== 1) return; // only required
      const key = f.fieldName;
      const dt = (f.dataType || "").toLowerCase().trim();
      const v = valuesObj[key];

      if (dt === "text box") {
        if (v == null || String(v).trim() === "") cfErrors[key] = "Required";
      } else if (dt === "single select") {
        if (v == null || String(v).trim() === "") cfErrors[key] = "Required";
      } else if (dt === "multi select") {
        if (!Array.isArray(v) || v.length === 0)
          cfErrors[key] = "Select at least one option";
      }
    });
    return cfErrors;
  };

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      contactNumber: "",
      emailId: "",
      resumesourceId: "",
      // candidateStatus: '',
      linkedIn: "",
    },
    validationSchema,
    validate: () => {
      const cfErrors = validateMandatoryCustomFields(
        customFieldsArray,
        getcustomFields,
      );
      return Object.keys(cfErrors).length ? { customFields: cfErrors } : {};
    },
    onSubmit: async (values) => {
      const cfErrors = validateMandatoryCustomFields(
        customFieldsArray,
        getcustomFields,
      );
      if (Object.keys(cfErrors).length > 0) {
        formik.setErrors((prev) => ({
          ...(prev || {}),
          customFields: cfErrors,
        }));
        formik.setTouched(
          (prev) => ({
            ...(prev || {}),
            customFields: Object.fromEntries(
              Object.keys(cfErrors).map((k) => [k, true]),
            ),
          }),
          true,
        );
        return;
      }

      const payload = {
        ...getDetails,
        Applicants: {
          ...getDetails.Applicants,
          ...values,
        },
        customFields: JSON.stringify(
          Object.entries(getcustomFields).map(([key, value]) => ({
            key,
            value,
          })),
        ),
      };

      setDisableBtn(true);
      await onValidData(payload);
    },
  });

  /* -------------------- EFFECTS -------------------- */

  // Reset
  useEffect(() => {
    if (JSON.stringify(input) === JSON.stringify(iniValue)) {
      formik.resetForm();
    }
    if (
      input !== undefined &&
      input !== null &&
      customFieldsData &&
      customFieldsData.length > 0
    ) {
      //&& input.Applicants.firstName !== ""
      if (typeof input.skillDetails === "string") {
        input.skillDetails = JSON.parse(input.skillDetails);
      }
      formik.values.firstName = input.Applicants.firstName
        ? input.Applicants.firstName
        : "";
      formik.values.lastName = input.Applicants.lastName
        ? input.Applicants.lastName
        : "";
      formik.values.contactNumber = input.Applicants.contactNumber
        ? input.Applicants.contactNumber
        : "";
      formik.values.emailId = input.Applicants.emailId
        ? input.Applicants.emailId
        : "";
      formik.values.linkedIn = input.linkedIn ? input.linkedIn : "";
      formik.values.resumesourceId = input.Applicants.resumesourceId
        ? input.Applicants.resumesourceId
        : "";
      formik.values.candidateStatus = input.Applicants.candidateStatus
        ? input.Applicants.candidateStatus
        : "";
      formik.values.totalExperience = input.Applicants.totalExperience
        ? input.Applicants.totalExperience
        : "";
      formik.values.formattedLocation = input.formattedLocation
        ? input.formattedLocation
        : "";
      formik.values.locationRawInput = input.locationRawInput
        ? input.locationRawInput
        : "";
      formik.values.locationCity = input.locationCity
        ? input.locationCity
        : "";
      formik.values.locationState = input.locationState
        ? input.locationState
        : "";
      formik.values.locationCountry = input.locationCountry
        ? input.locationCountry
        : "";
      formik.values.latitude = input.latitude
        ? input.latitude
        : "";
      formik.values.longitude = input.longitude
        ? input.longitude
        : "";

      // for customfields
      if (customFieldsData && customFieldsData.length > 0) {
        const data = JSON.parse(JSON.stringify(customFieldsData));
        let customPostingFieldsData = data.filter(
          (x) => x.moduleId.toString() === "2" && x.fieldType === "custom",
        );

        if (componentType !== "Create" && input.customFields) {
          let customFieldsData = safeJSONParse(JSON.parse(input.customFields));
          let formatCustomFields = {};
          if (customFieldsData && customFieldsData.length > 0) {
            customFieldsData.forEach((list) => {
              if (customPostingFieldsData.length > 0) {
                customPostingFieldsData.forEach((item) => {
                  if (item.fieldName === list.key) {
                    if (item.dataType.toLowerCase().trim() !== "text box") {
                      if (
                        item.dataType.toLowerCase().trim() === "single select"
                      ) {
                        if (typeof list.value === "string") {
                          formatCustomFields[list.key] = list.value;
                        } else {
                          const commonValues = JSON.parse(
                            item.customValues,
                          ).filter((value) => {
                            const valStr = (list.value ?? "").toString();
                            return valStr.includes(value);
                          });
                          formatCustomFields[list.key] =
                            commonValues.length > 0 ? commonValues[0] : [];
                        }
                      } else {
                        if (typeof list.value === "string") {
                          formatCustomFields[list.key] = JSON.parse(
                            item.customValues,
                          ).includes(list.value)
                            ? [list.value]
                            : [];
                        } else {
                          const commonValues = JSON.parse(
                            item.customValues,
                          ).filter((value) => {
                            if (Array.isArray(list.value)) {
                              return list.value.includes(value);
                            }
                            if (typeof list.value === "string") {
                              return list.value.includes(value);
                            }
                            return false;
                          });
                          formatCustomFields[list.key] =
                            commonValues.length > 0 ? commonValues : [];
                        }
                      }
                    } else {
                      formatCustomFields[list.key] = list.value;
                    }
                  }
                });
              } else {
                formatCustomFields[list.key] = list.value; // (typeof list.value === 'string') ? [list.value] :
              }
            });
            console.log("formatCustomFields +++++++", formatCustomFields);
            setGetCustomFields(formatCustomFields);
          }
        }
      }
      if (
        input.source === "Job Board Portal" ||
        input.source === "Cloud Sourcing" ||
        input.source === "Employee Portal" ||
        input.source === "Vendor Sourcing"
      ) {
        setIsPortals(true);
        formik.values.resumesourceId = "10";
        input.Applicants.resumesourceId = "10";
        setPortalFrom(input.source);
      }
      setGetDetails(input);
      setDisableBtn(false);
    }
  }, [input, iniValue]);

  // Populate form
  useEffect(() => {
    if (!input) return;

    const applicants = input.Applicants || {};

    formik.setValues({
      firstName: applicants.firstName || "",
      lastName: applicants.lastName || "",
      contactNumber: applicants.contactNumber || "",
      emailId: applicants.emailId || "",
      resumesourceId: applicants.resumesourceId || "",
      candidateStatus: applicants.candidateStatus || "",
      linkedIn: input.linkedIn || "",
      formattedLocation: input.formattedLocation || "",
      locationRawInput: input.locationRawInput || "",
      locationCity: input.locationCity || "",
      locationState: input.locationState || "",
      locationCountry: input.locationCountry || "",
      latitude: input.latitude || "",
      longitude: input.longitude || "",
    });

    setGetDetails({
      ...input,
      skillDetails: safeJSONParse(input.skillDetails, []),
      certification: safeJSONParse(input.certification),
    });

    setDisableBtn(false);
  }, [input]);

  // Portal handling
  useEffect(() => {
    if (!input?.source) return;

    if (PORTAL_SOURCES.includes(input.source)) {
      setIsPortals(true);
      setPortalFrom(input.source);
      formik.setFieldValue("resumesourceId", "10", false);
    }
  }, [input?.source]);

  // Default dropdowns
  useEffect(() => {
    const modules = defaultApplicantsData.filter((x) => x.moduleId === "2");
    modules.forEach((m) => {
      if (m.fieldName === "Source") setSourceModelValues(m.customValues);
      if (m.fieldName === "Candidate status")
        setCandidateStatusValues(m.customValues);
    });
  }, []);

  // Custom fields setup
  useEffect(() => {
    if (customFieldsData && customFieldsData.length > 0) {
      const data = JSON.parse(JSON.stringify(customFieldsData));
      let customPostingFieldsData = data.filter(
        (x) => x.moduleId.toString() === "2" && x.fieldType === "custom",
      );
      if (customPostingFieldsData.length > 0) {
        let Array = {};
        customPostingFieldsData.forEach((item) => {
          item.customValues = JSON.parse(item.customValues);
          item.moduleId = item.moduleId.toString();
          Array[item.fieldName] =
            item.dataType.toLowerCase() !== "text box" ? [] : "";
        });
        setCustomFieldsArray(customPostingFieldsData);
        if (componentType !== "view") {
          setGetCustomFields(Array);
        }
      }
      let overrideDefaultPostingFieldsData = data.filter(
        (x) => x.moduleId.toString() === "2" && x.fieldType === "default",
      );
      if (overrideDefaultPostingFieldsData.length > 0) {
        overrideDefaultPostingFieldsData.forEach((list) => {
          if (list.fieldName === "Source") {
            let updatedCustomValues = JSON.parse(list.customValues);
            let modifiedCustomValues = [];
            updatedCustomValues.forEach((list) => {
              if (
                list !== "Cloud Sourcing" &&
                list !== "Employee Portal" &&
                list !== "Vendor Sourcing" &&
                list !== "Job Board Portal"
              ) {
                modifiedCustomValues.push(list);
              }
            });
            setSourceModelValues(modifiedCustomValues);
          }
          if (list.fieldName === "Candidate status") {
            setCandidateStatusValues(JSON.parse(list.customValues));
          }
        });
      }
    }
  }, [customFieldsData, componentType]);

  /* -------------------- HANDLERS -------------------- */

  const onChangeHandler = (data, type) => {
    let details = JSON.parse(JSON.stringify(getDetails));
    if (type === "emailId") {
      details.Applicants[type] = data.trim();
      formik.values[type] = data.trim();
    } else {
      if (type === "contactNumber" || type === "alternateContactNumber") {
        details.Applicants[type] = data.trimStart();
        formik.values[type] = data.trimStart();
      } else if (type === "totalExperience") {
        details[type] = data.trimStart();
        formik.values[type] = data.trimStart();
      } else if (type === "linkedIn") {
        details[type] = data.trim();
        formik.values[type] = data.trim();
      } else if (type === "resumesourceId") {
        details.Applicants["resumesource"] =
          sourceModelValues[parseInt(data) - 1];
        details.Applicants[type] = data.trimStart();
        formik.values[type] = data.trimStart();
      } else if(type === "formattedLocation" || type === "locationCity" || type === "locationState" || type === "locationCountry" || type === "latitude" || type === "longitude"){
        details[type] = data.trimStart();
        formik.values[type] = data.trimStart();
      } else {
        details.Applicants[type] = data.trimStart();
        formik.values[type] = data.trimStart();
      }
    }
    setGetDetails(details);
  };

  const onChangeCustomFieldsHandler = (value, key) => {
    setGetCustomFields((prev) => ({ ...prev, [key]: value }));
  };

  const onTableData = (data, type) => {
    let Array = JSON.parse(JSON.stringify(getDetails));
    Array[type] = data;
    setGetDetails(Array);
  };

  const onRemoveHandler = (item, index, type) => {
    let details = JSON.parse(JSON.stringify(getDetails));
    details[type].splice(index, 1);
    setGetDetails(details);
  };

  const getSelectedValue = (key) => {
    const val = getcustomFields?.[key];
    if (!val) return "";
    if (Array.isArray(val)) {
      return val[0]?.toString() || ""; // .toLowerCase()
    }
    return val.toString(); // .toLowerCase()
  };

  const clearInvalidCustomFieldValue = (fieldName, values) => {
    const currentValue = getcustomFields?.[fieldName]; // normalizeValue(getcustomFields?.[fieldName]);
    if (!currentValue) return;
    const validValues = values || []; // .map((v) => v.toLowerCase())
    if (!validValues.includes(currentValue)) {
      onChangeCustomFieldsHandler("", fieldName);
    }
  };

  const handleAddSkill = (value) => {
    const skill = value.trim();
    if (!skill) return;

    let details = JSON.parse(JSON.stringify(getDetails));

    details.skillDetails.push({
      name: skill,
      numberOfMonths: null,
      lastUsed: "",
    });

    setGetDetails(details);
    setAddSkills("");
  };

  return (
    <React.Fragment>
      {componentType !== "Create" ? (
        <div style={{ textAlign: "right", margin: "19px 0" }}>
          {actions &&
            actions.map((item) => {
              if (item.type === "back") {
                return (
                  <Button
                    variant={item.variant}
                    className={item.class}
                    style={item.style}
                    onClick={item.navigateTo}
                    size="small"
                  >
                    <Link
                      href="#"
                      style={{ color: "red", fontSize: "14px" }}
                      underline="always"
                    >
                      {" "}
                      {item.label}
                      {""}
                    </Link>
                  </Button>
                );
              } else if (item.type === "submit") {
                return (
                  <Button
                    variant={item.variant}
                    className={item.class}
                    disabled={disableBtn}
                    style={item.style}
                    onClick={formik.handleSubmit}
                    size="small"
                  >
                    {item.label}
                    {""}
                  </Button>
                );
              }
            })}
        </div>
      ) : (
        ""
      )}
      <FocusError formik={formik} />
      <div
        style={
          isMobile
            ? {
                width: "calc(100% - 32px)",
                margin: "0 auto",
                paddingBottom: "70px",
              }
            : { width: "98%", margin: "0 auto", paddingBottom: "20px" }
        }
        className={
          componentType === "view"
            ? "viewSection applicant-details-section"
            : "applicant-details-section"
        }
      >
        <div className="personal-details-section">
          <div className="personaldetails">
            <h3
              className="personaldetailstext"
              style={
                isMobile
                  ? {
                      fontSize: "16px",
                      fontWeight: "600",
                      color: "#1e293b",
                      marginTop: 0,
                      marginBottom: "24px",
                    }
                  : {}
              }
            >
              {isMobile ? "Personal Information" : "Personal Details"}
            </h3>
          </div>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                lg: "repeat(5, 1fr)",
              },
              gap: isMobile ? "15px" : "45px",
              mt: isMobile ? "0px" : "60px",
            }}
          >
            <div style={{ width: "100%" }}>
              {isMobile && (
                <Typography
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#4d5156",
                    marginBottom: "8px",
                  }}
                >
                  First Name *
                </Typography>
              )}
              <TextField
                className="boldlabel"
                error={
                  formik.errors.firstName && formik.touched.firstName
                    ? true
                    : false
                }
                helperText={
                  formik.errors.firstName && formik.touched.firstName
                    ? formik.errors.firstName
                    : ""
                }
                id="outlined-basic"
                label={isMobile ? "" : "First Name*"}
                variant="outlined"
                style={{ width: "100%" }}
                name="firstName"
                autoComplete="off"
                value={
                  getDetails.Applicants.firstName
                    ? getDetails.Applicants.firstName
                    : ""
                }
                InputLabelProps={
                  isMobile
                    ? undefined
                    : {
                        shrink: true,
                      }
                }
                sx={
                  isMobile
                    ? {
                        "& .MuiOutlinedInput-root": { backgroundColor: "#fff" },
                        "& .MuiOutlinedInput-input": {
                          padding: "12px 14px",
                          fontSize: "14px",
                        },
                      }
                    : {}
                }
                disabled={componentType === "view" ? true : false}
                onChange={(e) => onChangeHandler(e.target.value, "firstName")}
                onBlur={formik.handleBlur}
              />
            </div>
            <div style={{ width: "100%" }}>
              {isMobile && (
                <Typography
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#4d5156",
                    marginBottom: "8px",
                  }}
                >
                  Last Name *
                </Typography>
              )}
              <TextField
                className="boldlabel"
                error={
                  formik.errors.lastName && formik.touched.lastName
                    ? true
                    : false
                }
                helperText={
                  formik.errors.lastName && formik.touched.lastName
                    ? formik.errors.lastName
                    : ""
                }
                id="outlined-basic"
                label={isMobile ? "" : "Last Name*"}
                variant="outlined"
                style={{ width: "100%" }}
                name="lastName"
                autoComplete="off"
                value={
                  getDetails.Applicants.lastName
                    ? getDetails.Applicants.lastName
                    : ""
                }
                InputLabelProps={
                  isMobile
                    ? undefined
                    : {
                        shrink: true,
                      }
                }
                sx={
                  isMobile
                    ? {
                        "& .MuiOutlinedInput-root": { backgroundColor: "#fff" },
                        "& .MuiOutlinedInput-input": {
                          padding: "12px 14px",
                          fontSize: "14px",
                        },
                      }
                    : {}
                }
                disabled={componentType === "view" ? true : false}
                onChange={(e) => onChangeHandler(e.target.value, "lastName")}
                onBlur={formik.handleBlur}
              />
            </div>
            <div style={{ width: "100%" }}>
              {isMobile && (
                <Typography
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#4d5156",
                    marginBottom: "8px",
                  }}
                >
                  Contact Number *
                </Typography>
              )}
              <TextField
                className="boldlabel"
                error={
                  formik.errors.contactNumber && formik.touched.contactNumber
                    ? true
                    : false
                }
                helperText={
                  formik.errors.contactNumber && formik.touched.contactNumber
                    ? formik.errors.contactNumber
                    : ""
                }
                id="outlined-basic"
                label={isMobile ? "" : "Contact Number*"}
                variant="outlined"
                style={{ width: "100%" }}
                name="contactNumber"
                autoComplete="off"
                value={
                  getDetails.Applicants.contactNumber
                    ? getDetails.Applicants.contactNumber
                    : ""
                }
                InputLabelProps={
                  isMobile
                    ? undefined
                    : {
                        shrink: true,
                      }
                }
                sx={
                  isMobile
                    ? {
                        "& .MuiOutlinedInput-root": { backgroundColor: "#fff" },
                        "& .MuiOutlinedInput-input": {
                          padding: "12px 14px",
                          fontSize: "14px",
                        },
                      }
                    : {}
                }
                disabled={componentType === "view" ? true : false}
                onChange={(e) =>
                  onChangeHandler(e.target.value, "contactNumber")
                }
                onBlur={formik.handleBlur}
              />
            </div>
            <div style={{ width: "100%" }}>
              {isMobile && (
                <Typography
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#4d5156",
                    marginBottom: "8px",
                  }}
                >
                  Alternate Contact
                </Typography>
              )}
              <TextField
                className="boldlabel"
                error={
                  formik.errors.alternateContactNumber &&
                  formik.touched.alternateContactNumber
                    ? true
                    : false
                }
                helperText={
                  formik.errors.alternateContactNumber &&
                  formik.touched.alternateContactNumber
                    ? formik.errors.alternateContactNumber
                    : ""
                }
                id="outlined-basic"
                label={
                  isMobile
                    ? ""
                    : "Alternate Contact Number \u00a0\u00a0\u00a0\u00a0\u00a0\u00a0"
                }
                variant="outlined"
                style={{ width: "100%" }}
                name="alternateContactNumber"
                autoComplete="off"
                // shrink={componentType !== "Create" ? true : false}
                InputLabelProps={
                  isMobile
                    ? undefined
                    : {
                        shrink: true,
                      }
                }
                sx={
                  isMobile
                    ? {
                        "& .MuiOutlinedInput-root": { backgroundColor: "#fff" },
                        "& .MuiOutlinedInput-input": {
                          padding: "12px 14px",
                          fontSize: "14px",
                        },
                      }
                    : {}
                }
                value={
                  getDetails.Applicants.alternateContactNumber
                    ? getDetails.Applicants.alternateContactNumber
                    : ""
                }
                disabled={componentType === "view" ? true : false}
                onChange={(e) =>
                  onChangeHandler(e.target.value, "alternateContactNumber")
                }
                onBlur={formik.handleBlur}
              />
            </div>
            <div style={{ width: "100%" }}>
              {isMobile && (
                <Typography
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#4d5156",
                    marginBottom: "8px",
                  }}
                >
                  Email *
                </Typography>
              )}
              <TextField
                className="boldlabel"
                error={
                  formik.errors.emailId && formik.touched.emailId ? true : false
                }
                helperText={
                  formik.errors.emailId && formik.touched.emailId
                    ? formik.errors.emailId
                    : ""
                }
                id="outlined-basic"
                label={isMobile ? "" : "E-mail Address*"}
                variant="outlined"
                style={{ width: "100%" }}
                name="emailId"
                autoComplete="off"
                value={
                  getDetails.Applicants.emailId
                    ? getDetails.Applicants.emailId
                    : ""
                }
                InputLabelProps={
                  isMobile
                    ? undefined
                    : {
                        shrink: true,
                      }
                }
                sx={
                  isMobile
                    ? {
                        "& .MuiOutlinedInput-root": { backgroundColor: "#fff" },
                        "& .MuiOutlinedInput-input": {
                          padding: "12px 14px",
                          fontSize: "14px",
                        },
                      }
                    : {}
                }
                disabled={componentType === "view" ? true : false}
                onChange={(e) => onChangeHandler(e.target.value, "emailId")}
                onBlur={formik.handleBlur}
              />
            </div>
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                lg: "repeat(4, 1fr)",
              },
              columnGap: "24px",
              rowGap: isMobile ? "15px" : "40px",
              mt: isMobile ? "20px" : "60px",
            }}
          >
            <div style={{ width: "100%" }}>
              <FormControl
                fullWidth
                error={
                  formik.errors.resumesourceId && formik.touched.resumesourceId
                    ? true
                    : false
                }
              >
                {isMobile ? (
                  <Typography
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#4d5156",
                      marginBottom: "8px",
                    }}
                  >
                    Sourced from &nbsp;*
                  </Typography>
                ) : (
                  <InputLabel className="boldSelectlabel" shrink={false}>
                    Sourced From &nbsp;*
                  </InputLabel>
                )}
                <Select
                  value={
                    isPortals === true
                      ? "10"
                      : getDetails.Applicants.resumesourceId
                        ? getDetails.Applicants.resumesourceId
                        : ""
                  }
                  name="Sourced From"
                  autoComplete="off"
                  label={isMobile ? "" : <span>Sourced From &nbsp;*</span>}
                  disabled={
                    componentType !== "Create" || isPortals === true
                      ? true
                      : false
                  }
                  onChange={(e) =>
                    onChangeHandler(e.target.value, "resumesourceId")
                  }
                  style={{
                    width: "100%",
                    height: isMobile ? "46px" : "3.5rem",
                    borderRadius: "4px",
                    backgroundColor: isMobile ? "#fff" : "transparent",
                  }}
                  onFocus={formik.handleBlur}
                >
                  {sourceModelValues.map((name, index) => (
                    <MenuItem
                      className="selectOptions"
                      key={name}
                      value={(index + 1).toString()}
                    >
                      {name}
                    </MenuItem>
                  ))}
                  {isPortals === true && (
                    <MenuItem className="selectOptions" key="10" value="10">
                      {portalFrom}
                    </MenuItem>
                  )}
                </Select>
                <FormHelperText>
                  {formik.errors.resumesourceId && formik.touched.resumesourceId
                    ? formik.errors.resumesourceId
                    : ""}
                </FormHelperText>
              </FormControl>
            </div>
            <div style={{ width: "100%" }}>
              {isMobile && (
                <Typography
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#4d5156",
                    marginBottom: "8px",
                  }}
                >
                  Total Years Of Experience
                </Typography>
              )}
              <TextField
                className="boldlabel"
                error={
                  formik.errors.totalExperience &&
                  formik.touched.totalExperience
                    ? true
                    : false
                }
                helperText={
                  formik.errors.totalExperience &&
                  formik.touched.totalExperience
                    ? formik.errors.totalExperience
                    : ""
                }
                id="outlined-basic"
                label={
                  isMobile
                    ? ""
                    : "Total Years Of Experience \u00a0\u00a0\u00a0\u00a0\u00a0\u00a0\u00a0"
                }
                variant="outlined"
                style={{ width: "100%" }}
                name="totalExperience"
                autoComplete="off"
                // shrink={componentType !== "Create" ? true : false}
                InputLabelProps={
                  isMobile
                    ? undefined
                    : {
                        shrink: true,
                      }
                }
                sx={
                  isMobile
                    ? {
                        "& .MuiOutlinedInput-root": { backgroundColor: "#fff" },
                        "& .MuiOutlinedInput-input": {
                          padding: "12px 14px",
                          fontSize: "14px",
                        },
                      }
                    : {}
                }
                value={
                  getDetails.totalExperience ? getDetails.totalExperience : ""
                }
                disabled={componentType === "view" ? true : false}
                onChange={(e) =>
                  onChangeHandler(e.target.value, "totalExperience")
                }
                onBlur={formik.handleBlur}
              />
            </div>
            <div style={{ width: "100%" }}>
              {isMobile && (
                <Typography
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#4d5156",
                    marginBottom: "8px",
                  }}
                >
                  LinkedIn URL
                </Typography>
              )}
              <TextField
                className="boldlabel"
                error={
                  formik.errors.linkedIn && formik.touched.linkedIn
                    ? true
                    : false
                }
                helperText={
                  formik.errors.linkedIn && formik.touched.linkedIn
                    ? formik.errors.linkedIn
                    : ""
                }
                id="outlined-basic"
                label={isMobile ? "" : "LinkedIn URL \u00a0\u00a0\u00a0\u00a0"}
                variant="outlined"
                style={{ width: "100%" }}
                name="linkedIn"
                autoComplete="off"
                value={getDetails.linkedIn ? getDetails.linkedIn : ""}
                InputLabelProps={
                  isMobile
                    ? undefined
                    : {
                        shrink: true,
                      }
                }
                sx={
                  isMobile
                    ? {
                        "& .MuiOutlinedInput-root": { backgroundColor: "#fff" },
                        "& .MuiOutlinedInput-input": {
                          padding: "12px 14px",
                          fontSize: "14px",
                        },
                      }
                    : {}
                }
                disabled={componentType === "view" ? true : false}
                onChange={(e) => onChangeHandler(e.target.value, "linkedIn")}
                onBlur={formik.handleBlur}
              />
            </div>
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                lg: "repeat(5, 1fr)",
              },
              columnGap: "24px",
              rowGap: isMobile ? "15px" : "40px",
              mt: isMobile ? "20px" : "60px",
            }}
          >
            <Box sx={{ width: "100%", gridColumn: { lg: "span 2" } }}>
              {isMobile && (
                <Typography
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#4d5156",
                    marginBottom: "8px",
                  }}
                >
                  Formatted Location
                </Typography>
              )}
              <TextField
                className="boldlabel"
                error={
                  formik.errors.formattedLocation && formik.touched.formattedLocation
                    ? true
                    : false
                }
                helperText={
                  formik.errors.formattedLocation && formik.touched.formattedLocation
                    ? formik.errors.formattedLocation
                    : ""
                }
                id="outlined-basic"
                label={isMobile ? "" : "Formatted Location \u00a0\u00a0\u00a0\u00a0"}
                variant="outlined"
                style={{ width: "100%" }}
                name="formattedLocation"
                autoComplete="off"
                value={getDetails.formattedLocation ? getDetails.formattedLocation : ""}
                InputLabelProps={
                  isMobile
                    ? undefined
                    : {
                        shrink: true,
                      }
                }
                sx={
                  isMobile
                    ? {
                        "& .MuiOutlinedInput-root": { backgroundColor: "#fff" },
                        "& .MuiOutlinedInput-input": {
                          padding: "12px 14px",
                          fontSize: "14px",
                        },
                      }
                    : {}
                }
                disabled={componentType === "view" ? true : false}
                onChange={(e) => onChangeHandler(e.target.value, "formattedLocation")}
                onBlur={formik.handleBlur}
              />
            </Box>
            <div style={{ width: "100%" }}>
              {isMobile && (
                <Typography
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#4d5156",
                    marginBottom: "8px",
                  }}
                >
                  City
                </Typography>
              )}
              <TextField
                className="boldlabel"
                error={
                  formik.errors.locationCity && formik.touched.locationCity
                    ? true
                    : false
                }
                helperText={
                  formik.errors.locationCity && formik.touched.locationCity
                    ? formik.errors.locationCity
                    : ""
                }
                id="outlined-basic"
                label={isMobile ? "" : "City \u00a0\u00a0\u00a0\u00a0"}
                variant="outlined"
                style={{ width: "100%" }}
                name="locationCity"
                autoComplete="off"
                value={getDetails.locationCity ? getDetails.locationCity : ""}
                InputLabelProps={
                  isMobile
                    ? undefined
                    : {
                        shrink: true,
                      }
                }
                sx={
                  isMobile
                    ? {
                        "& .MuiOutlinedInput-root": { backgroundColor: "#fff" },
                        "& .MuiOutlinedInput-input": {
                          padding: "12px 14px",
                          fontSize: "14px",
                        },
                      }
                    : {}
                }
                disabled={componentType === "view" ? true : false}
                onChange={(e) => onChangeHandler(e.target.value, "locationCity")}
                onBlur={formik.handleBlur}
              />
            </div>
            <div style={{ width: "100%" }}>
              {isMobile && (
                <Typography
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#4d5156",
                    marginBottom: "8px",
                  }}
                >
                  State
                </Typography>
              )}
              <TextField
                className="boldlabel"
                error={
                  formik.errors.locationState && formik.touched.locationState
                    ? true
                    : false
                }
                helperText={
                  formik.errors.locationState && formik.touched.locationState
                    ? formik.errors.locationState
                    : ""
                }
                id="outlined-basic"
                label={isMobile ? "" : "State \u00a0\u00a0\u00a0\u00a0"}
                variant="outlined"
                style={{ width: "100%" }}
                name="locationState"
                autoComplete="off"
                value={getDetails.locationState ? getDetails.locationState : ""}
                InputLabelProps={
                  isMobile
                    ? undefined
                    : {
                        shrink: true,
                      }
                }
                sx={
                  isMobile
                    ? {
                        "& .MuiOutlinedInput-root": { backgroundColor: "#fff" },
                        "& .MuiOutlinedInput-input": {
                          padding: "12px 14px",
                          fontSize: "14px",
                        },
                      }
                    : {}
                }
                disabled={componentType === "view" ? true : false}
                onChange={(e) => onChangeHandler(e.target.value, "locationState")}
                onBlur={formik.handleBlur}
              />
            </div>
            <div style={{ width: "100%" }}>
              {isMobile && (
                <Typography
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#4d5156",
                    marginBottom: "8px",
                  }}
                >
                  Country
                </Typography>
              )}
              <TextField
                className="boldlabel"
                error={
                  formik.errors.locationCountry && formik.touched.locationCountry
                    ? true
                    : false
                }
                helperText={
                  formik.errors.locationCountry && formik.touched.locationCountry
                    ? formik.errors.locationCountry
                    : ""
                }
                id="outlined-basic"
                label={isMobile ? "" : "Country \u00a0\u00a0\u00a0\u00a0"}
                variant="outlined"
                style={{ width: "100%" }}
                name="locationCountry"
                autoComplete="off"
                value={getDetails.locationCountry ? getDetails.locationCountry : ""}
                InputLabelProps={
                  isMobile
                    ? undefined
                    : {
                        shrink: true,
                      }
                }
                sx={
                  isMobile
                    ? {
                        "& .MuiOutlinedInput-root": { backgroundColor: "#fff" },
                        "& .MuiOutlinedInput-input": {
                          padding: "12px 14px",
                          fontSize: "14px",
                        },
                      }
                    : {}
                }
                disabled={componentType === "view" ? true : false}
                onChange={(e) => onChangeHandler(e.target.value, "locationCountry")}
                onBlur={formik.handleBlur}
              />
            </div>
            <Box sx={{ width: "100%", gridColumn: { lg: "span 2" } }}>
              {isMobile && (
                <Typography
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#4d5156",
                    marginBottom: "8px",
                  }}
                >
                  Latitude
                </Typography>
              )}
              <TextField
                className="boldlabel"
                error={
                  formik.errors.latitude && formik.touched.latitude
                    ? true
                    : false
                }
                helperText={
                  formik.errors.latitude && formik.touched.latitude
                    ? formik.errors.latitude
                    : ""
                }
                id="outlined-basic"
                label={isMobile ? "" : "Latitude \u00a0\u00a0\u00a0\u00a0"}
                variant="outlined"
                style={{ width: "100%" }}
                name="latitude"
                autoComplete="off"
                value={getDetails.latitude ? getDetails.latitude : ""}
                InputLabelProps={
                  isMobile
                    ? undefined
                    : {
                        shrink: true,
                      }
                }
                sx={
                  isMobile
                    ? {
                        "& .MuiOutlinedInput-root": { backgroundColor: "#fff" },
                        "& .MuiOutlinedInput-input": {
                          padding: "12px 14px",
                          fontSize: "14px",
                        },
                      }
                    : {}
                }
                disabled={componentType === "view" ? true : false}
                onChange={(e) => onChangeHandler(e.target.value, "latitude")}
                onBlur={formik.handleBlur}
              />
            </Box>
            <Box sx={{ width: "100%", gridColumn: { lg: "span 2" } }}>
              {isMobile && (
                <Typography
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#4d5156",
                    marginBottom: "8px",
                  }}
                >
                  Longitude
                </Typography>
              )}
              <TextField
                className="boldlabel"
                error={
                  formik.errors.longitude && formik.touched.longitude
                    ? true
                    : false
                }
                helperText={
                  formik.errors.longitude && formik.touched.longitude
                    ? formik.errors.longitude
                    : ""
                }
                id="outlined-basic"
                label={isMobile ? "" : "Longitude \u00a0\u00a0\u00a0\u00a0"}
                variant="outlined"
                style={{ width: "100%" }}
                name="longitude"
                autoComplete="off"
                value={getDetails.longitude ? getDetails.longitude : ""}
                InputLabelProps={
                  isMobile
                    ? undefined
                    : {
                        shrink: true,
                      }
                }
                sx={
                  isMobile
                    ? {
                        "& .MuiOutlinedInput-root": { backgroundColor: "#fff" },
                        "& .MuiOutlinedInput-input": {
                          padding: "12px 14px",
                          fontSize: "14px",
                        },
                      }
                    : {}
                }
                disabled={componentType === "view" ? true : false}
                onChange={(e) => onChangeHandler(e.target.value, "longitude")}
                onBlur={formik.handleBlur}
              />
            </Box>
            
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                lg: "repeat(4, 1fr)",
              },
              columnGap: "24px",
              rowGap: isMobile ? "15px" : "40px",
              mt: isMobile ? "20px" : "60px",
            }}
          >
            {customFieldsArray &&
              customFieldsArray.length > 0 &&
              customFieldsArray.map((data, index) => {
                const err = formik.errors?.customFields?.[data.fieldName];
                const touched = formik.touched?.customFields?.[data.fieldName];
                const hasError = Boolean(
                  err && (touched || formik.submitCount > 0),
                );
                const isReq = Number(data.mandatory) === 1;
                if (data.dataType.toLowerCase().trim() === "text box") {
                  return (
                    <div style={{ width: "100%", marginBottom: "3.2%" }}>
                      {isMobile && (
                        <Typography
                          style={{
                            fontSize: "14px",
                            fontWeight: "600",
                            color: "#4d5156",
                            marginBottom: "8px",
                          }}
                        >
                          {`${data.fieldName}${isReq ? "*" : ""}`}
                        </Typography>
                      )}
                      <TextField
                        className="boldlabel"
                        id={data.fieldName}
                        variant="outlined"
                        disabled={componentType === "view" ? true : false}
                        autoComplete="off"
                        style={{ width: "100%" }}
                        label={
                          isMobile
                            ? ""
                            : `${data.fieldName}${Number(data.mandatory) === 1 ? "*" : ""}`
                        }
                        error={hasError}
                        helperText={hasError ? err : ""}
                        name={`customFields.${data.fieldName}`}
                        InputLabelProps={
                          isMobile
                            ? undefined
                            : {
                                shrink: true,
                              }
                        }
                        sx={
                          isMobile
                            ? {
                                "& .MuiOutlinedInput-root": {
                                  backgroundColor: "#fff",
                                },
                                "& .MuiOutlinedInput-input": {
                                  padding: "12px 14px",
                                  fontSize: "14px",
                                },
                              }
                            : {}
                        }
                        // value={getcustomFields && getcustomFields[data.fieldName] ? getcustomFields[data.fieldName] : ''}
                        value={getcustomFields?.[data.fieldName] ?? ""}
                        onBlur={formik.handleBlur}
                        onChange={(e) =>
                          onChangeCustomFieldsHandler(
                            e.target.value,
                            data.fieldName,
                          )
                        }
                      />
                    </div>
                  );
                } else if (
                  data.dataType.toLowerCase().trim() === "multi select"
                ) {
                  return (
                    <div style={{ width: "100%", marginBottom: "3.2%" }}>
                      <FormControl
                        sx={{ width: "100%" }}
                        fullWidth
                        error={hasError}
                      >
                        {isMobile ? (
                          <Typography
                            style={{
                              fontSize: "14px",
                              fontWeight: "600",
                              color: "#4d5156",
                              marginBottom: "8px",
                            }}
                          >
                            {`${data.fieldName}${isReq ? "*" : ""}`}
                          </Typography>
                        ) : (
                          <InputLabel
                            className="boldSelectlabel"
                            shrink={false}
                            id={`${data.fieldName}-label`}
                          >
                            {data.fieldName}
                            {isReq ? "*" : ""}
                          </InputLabel>
                        )}
                        <Select
                          labelId={`${data.fieldName}-label`}
                          id={`${data.fieldName}-select`}
                          multiple
                          name={`customFields.${data.fieldName}`}
                          disabled={componentType === "view" ? true : false}
                          value={
                            getcustomFields && getcustomFields[data.fieldName]
                              ? getcustomFields[data.fieldName]
                              : []
                          }
                          onChange={(e) =>
                            onChangeCustomFieldsHandler(
                              e.target.value,
                              data.fieldName,
                            )
                          }
                          onFocus={formik.handleBlur}
                          input={
                            <OutlinedInput
                              id={`select-multiple-chip-${data.fieldName}`}
                              label={data.fieldName}
                            />
                          }
                          renderValue={(selected) => {
                            return (
                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 0.5,
                                  maxHeight: "65px",
                                  overflowX: "auto",
                                }}
                              >
                                {typeof selected !== "string" &&
                                  selected.map((value) => (
                                    <Chip key={value} label={value} />
                                  ))}
                              </Box>
                            );
                          }}
                        >
                          {data.customValues &&
                            data.customValues.length > 0 &&
                            data.customValues.map((name) => (
                              <MenuItem
                                className="selectOptions"
                                key={name}
                                value={name}
                              >
                                {name}
                              </MenuItem>
                            ))}
                        </Select>
                        <FormHelperText>{hasError ? err : ""}</FormHelperText>
                      </FormControl>
                    </div>
                  );
                } else if (
                  data.dataType.toLowerCase().trim() === "single select"
                ) {
                  return (
                    <div style={{ width: "100%", marginBottom: "3.2%" }}>
                      <FormControl fullWidth error={hasError}>
                        {isMobile ? (
                          <Typography
                            style={{
                              fontSize: "14px",
                              fontWeight: "600",
                              color: "#4d5156",
                              marginBottom: "8px",
                            }}
                          >
                            {`${data.fieldName}${isReq ? "*" : ""}`}
                          </Typography>
                        ) : (
                          <InputLabel
                            className="boldSelectlabel"
                            id={`${data.fieldName}-label`}
                            shrink={false}
                          >
                            {data.fieldName}
                            {isReq ? "*" : ""}
                          </InputLabel>
                        )}
                        <Select
                          name={`customFields.${data.fieldName}`}
                          labelId={`${data.fieldName}-label`}
                          label={data.fieldName}
                          id={`${data.fieldName}-select`}
                          autoComplete="off"
                          value={getSelectedValue(data.fieldName)}
                          variant="outlined"
                          style={{
                            width: "100%",
                            height: isMobile ? "46px" : "3.5rem",
                            borderRadius: "4px",
                            backgroundColor: isMobile ? "#fff" : "transparent",
                          }}
                          onChange={(e) =>
                            onChangeCustomFieldsHandler(
                              e.target.value,
                              data.fieldName,
                            )
                          }
                          onBlur={formik.handleBlur}
                          disabled={componentType === "view" ? true : false}
                        >
                          {clearInvalidCustomFieldValue(
                            data.fieldName,
                            data.customValues,
                          )}
                          {data.customValues &&
                            data.customValues.length > 0 &&
                            data.customValues.map((name) => {
                              return <MenuItem value={name}>{name}</MenuItem>;
                            })}
                        </Select>
                        <FormHelperText>{hasError ? err : ""}</FormHelperText>
                      </FormControl>
                    </div>
                  );
                }
              })}
          </Box>
        </div>
        <div className="educationTable newApplicantEducation">
          <p className="educationdetailstext"> Education Details </p>
          <ApplicantsDynamicTable
            componentType={componentType}
            typeofTable="educationDetails"
            columns={educationColoumn}
            data={getDetails.educationDetails}
            showNewButton={true}
            receiveProps={(data) => onTableData(data, "educationDetails")}
          />
        </div>
        <div className="educationTable newApplicantWorkExp">
          <p className="workexperiencetext"> Work Experience </p>
          <ApplicantsDynamicTable
            componentType={componentType}
            typeofTable="workExp"
            columns={workExperiencesColoumn}
            data={getDetails.workExp}
            showNewButton={true}
            receiveProps={(data) => onTableData(data, "workExp")}
          />
        </div>
        <div className="additionalList">
          <div
            className="workexperiencetext"
            style={{ paddingBottom: "18px", margin: 0 }}
          >
            Skills
          </div>
          {componentType !== "view" && (
            <TextField
              id="outlined-basic"
              variant="outlined"
              style={{ width: "100%" }}
              name="addskills"
              placeholder="Add a skill and press Enter..."
              value={addSkills}
              autoComplete="off"
              onChange={(e) => setAddSkills(e.target.value)}
              inputProps={{
                enterKeyHint: "enter",
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.keyCode === 13) {
                  e.preventDefault();
                  e.stopPropagation();
                  handleAddSkill(e.target.value);
                }
              }}
              onBlur={(e) => {
                handleAddSkill(e.target.value);
              }}
            />
          )}
          <ul>
            {getDetails.skillDetails &&
              getDetails.skillDetails.length > 0 &&
              getDetails.skillDetails.map((item, i) => {
                return (
                  <li key={i}>
                    {item.name}{" "}
                    {componentType !== "view" && (
                      <CloseIcon
                        onClick={() => onRemoveHandler(item, i, "skillDetails")}
                      />
                    )}
                  </li>
                );
              })}
          </ul>
        </div>
        <div className="workexperienceSection">
          <div
            className="workexperiencetext"
            style={{
              paddingBottom: "18px",
              display: "flex",
              justifyContent: "space-between",
              margin: 0,
            }}
          >
            Certifications
          </div>
          {componentType !== "view" && (
            <TextField
              id="outlined-basic"
              variant="outlined"
              style={{ width: "100%", marginBottom: "16px" }}
              name="addCertification"
              placeholder="Please enter certification and press the enter button"
              value={addCertification}
              autoComplete="off"
              onChange={(e) => setAddCertification(e.target.value)}
              onKeyPress={(e) => {
                if (e.which === 13) {
                  const certificateValue = e.target.value.trim();
                  if (certificateValue) {
                    setAddCertification("");
                    let details = JSON.parse(JSON.stringify(getDetails));
                    details.certification.push(certificateValue);
                    setGetDetails(details);
                  }
                }
              }}
            />
          )}
          {getDetails.certification &&
            getDetails.certification.length > 0 &&
            getDetails?.certification?.map((item, i) => {
              return (
                <p key={i}>
                  {item}{" "}
                  {componentType !== "view" && (
                    <CloseIcon
                      onClick={() => onRemoveHandler(item, i, "certification")}
                    />
                  )}
                </p>
              );
            })}
        </div>
        {componentType === "Create" ? (
          <div
            style={{
              width: "fit-content",
              marginTop: "40px",
              marginLeft: "auto",
            }}
          >
            {actions &&
              actions.map((item) => {
                if (item.type === "back") {
                  return (
                    <Button
                      className={item.class}
                      variant={item.variant}
                      style={item.style}
                      onClick={() => navigate(item.navigateTo)}
                      size="small"
                    >
                      <Link
                        href="#"
                        style={{ color: "red", fontSize: "14px" }}
                        underline="always"
                      >
                        {" "}
                        {item.label}{" "}
                      </Link>
                    </Button>
                  );
                } else if (item.type === "submit") {
                  return (
                    <Button
                      className={item.class}
                      variant={item.variant}
                      disabled={disableBtn}
                      style={item.style}
                      onClick={formik.handleSubmit}
                      size="small"
                    >
                      {item.label}{" "}
                    </Button>
                  );
                }
              })}
          </div>
        ) : (
          ""
        )}
      </div>
    </React.Fragment>
  );
};

export default memo(ApplicantDetailsComponent);

const educationColoumn = [
  { id: 1, displayName: "Degree", name: "degree", width: "300px" },
  { id: 2, displayName: "School", name: "organization", width: "200px" },
  { id: 3, displayName: "Duration", name: "duration", width: "200px" },
  { id: 4, displayName: "Location", name: "locationFormated", width: "200px" },
  { id: 5, displayName: "Action", name: "newIcon", width: "150px" },
];

const workExperiencesColoumn = [
  { id: 1, displayName: "Title", name: "jobTitle", width: "300px" },
  { id: 2, displayName: "Employer", name: "organization", width: "200px" },
  { id: 4, displayName: "Duration", name: "duration", width: "200px" },
  { id: 3, displayName: "Employment Type", name: "empType", width: "200px" },
  { id: 5, displayName: "Action", name: "newIcon", width: "150px" },
];
